import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Box, Typography, Container, Button, Stack, CircularProgress } from '@mui/material';
import { PersonAdd, LinkOff } from '@mui/icons-material';
import { supabase } from '../supabase';
import { getAppStoreUrl } from '../appStoreUrl';

interface ProfilePreview {
  username: string;
  display_name: string | null;
}

type LinkState =
  | { kind: 'loading' }
  | { kind: 'expired' }
  | { kind: 'valid'; profile: ProfilePreview };

function AddFriendPage() {
  const { userId } = useParams<{ userId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [state, setState] = useState<LinkState>({ kind: 'loading' });

  // Only expose the deep link once we know the invite is valid. An expired
  // or tokenless URL must not open the app to a stale invite.
  const appLink =
    state.kind === 'valid' && userId && token
      ? `thirdplace://user/${userId.toLowerCase()}?token=${token.toLowerCase()}`
      : null;

  useEffect(() => {
    if (!appLink) return;
    // Update the Smart App Banner so its "Open" button also carries the path.
    const meta = document.querySelector('meta[name="apple-itunes-app"]');
    meta?.setAttribute('content', `app-id=6762038043, app-argument=${appLink}`);
    const timer = window.setTimeout(() => {
      window.location.href = appLink;
    }, 300);
    return () => {
      window.clearTimeout(timer);
      meta?.setAttribute('content', 'app-id=6762038043');
    };
  }, [appLink]);

  useEffect(() => {
    async function resolveInvite() {
      if (!userId || !token) {
        setState({ kind: 'expired' });
        return;
      }
      // The `invite_tokens` RLS policy hides expired rows, so a missing
      // row is indistinguishable from an expired one. Matching user_id
      // guards against token/userId pairs that came from different links.
      const { data: tokenRow } = await supabase
        .from('invite_tokens')
        .select('user_id')
        .eq('token', token.toLowerCase())
        .maybeSingle();
      if (!tokenRow || tokenRow.user_id.toLowerCase() !== userId.toLowerCase()) {
        setState({ kind: 'expired' });
        return;
      }
      const { data: profile } = await supabase
        .from('profiles')
        .select('username, display_name')
        .eq('user_id', userId.toLowerCase())
        .maybeSingle();
      if (!profile) {
        setState({ kind: 'expired' });
        return;
      }
      setState({ kind: 'valid', profile });
    }
    resolveInvite();
  }, [userId, token]);

  const isExpired = state.kind === 'expired';
  const heading = isExpired
    ? 'This invite link has expired'
    : state.kind === 'valid'
    ? `Add ${state.profile.display_name ?? '@' + state.profile.username} on Third Place`
    : 'Add a friend on Third Place';
  const subheading =
    state.kind === 'valid' ? `@${state.profile.username}` :
    isExpired ? 'Invite links stay active for one hour. Ask your friend to share a new one.' :
    null;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
      <Box
        sx={{
          background: isExpired
            ? 'linear-gradient(135deg, #616161 0%, #424242 50%, #212121 100%)'
            : 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
          px: 3,
          pt: { xs: 6, sm: 8 },
          pb: { xs: 5, sm: 6 },
        }}
      >
        <Container maxWidth="sm" disableGutters sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.15)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            {isExpired ? (
              <LinkOff sx={{ fontSize: 36, color: 'white' }} />
            ) : (
              <PersonAdd sx={{ fontSize: 36, color: 'white' }} />
            )}
          </Box>
          {state.kind === 'loading' ? (
            <CircularProgress sx={{ color: 'white' }} />
          ) : (
            <>
              <Typography variant="h4" fontWeight={700} sx={{ color: 'white', mb: 1 }}>
                {heading}
              </Typography>
              {subheading && (
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  {subheading}
                </Typography>
              )}
            </>
          )}
        </Container>
      </Box>

      <Container maxWidth="sm" sx={{ flex: 1, mt: -2, position: 'relative', zIndex: 1, pb: 4 }}>
        <Box sx={{ bgcolor: 'white', borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', p: 3 }}>
          <Stack spacing={2}>
            {appLink && (
              <Button
                variant="contained"
                size="large"
                href={appLink}
                fullWidth
                sx={{
                  textTransform: 'none',
                  borderRadius: 2.5,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  boxShadow: '0 2px 8px rgba(25,118,210,0.3)',
                }}
              >
                Open in Third Place
              </Button>
            )}
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {isExpired ? 'New to Third Place?' : "Don't have the app yet?"}
            </Typography>
            <Button
              variant="outlined"
              size="large"
              href={getAppStoreUrl()}
              fullWidth
              sx={{
                textTransform: 'none',
                borderRadius: 2.5,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              Get Third Place
            </Button>
          </Stack>
        </Box>
      </Container>

      <Box component="footer" sx={{ textAlign: 'center', py: 2 }}>
        <Link to="/" style={{ color: '#999', fontSize: 13, textDecoration: 'none' }}>
          mythird.place
        </Link>
      </Box>
    </Box>
  );
}

export default AddFriendPage;
